import {
  chatMessageOwner,
  messageStringType,
} from '../../store/adaptiveAssessment/adaptiveAssessmentSlice';
import { formValidation } from '../formValidation';

export const minToTime = (seconds: number) => {
  var value = seconds;

  var units = {
    day: 24 * 60,
    hr: 60,
    min: 1,
  };

  var result = [];

  for (let name in units) {
    var p = Math.floor(value / units[name as keyof typeof units]);
    if (p == 1) result.push(' ' + p + ' ' + name);
    if (p >= 2) result.push(' ' + p + ' ' + name + 's');
    value %= units[name as keyof typeof units];
  }

  return result;
};

export const stringToHtml = (id: string, str: string) => {
  var dom = document.createElement('div');
  dom.innerHTML = str;
  document?.getElementById(id)?.appendChild(dom);
};

export const isValidEmail = (email: string) => {
  if (
    email &&
    email.length > 1 &&
    formValidation.fields.email.isValid.pattern.test(email.trim())
  ) {
    return true;
  } else {
    return false;
  }
};

export const GoInFullscreen = () => {
  const element = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
  };

  if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen(); // For Safari on iOS
  } else if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen(); // For IE/Edge
  }
};

export const ExitFullScreen = () => {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
  };

  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen(); // For Safari on iOS
  } else if (doc.msExitFullscreen) {
    doc.msExitFullscreen(); // For IE/Edge
  }
};

export const getOptionsTransformedInHTML = (choice: any[]) => {
  const options: string[] = [];
  const optionString: string[] = [];
  choice.forEach((item) => {
    let str = ``;
    optionString.push(item?.option?.toUpperCase());
    str += item?.option?.toUpperCase();
    str += `:  `;
    str += item?.value;
    str = `<p>` + str + `</p>`;
    options.push(str);
  });
  return {
    options,
    optionString,
  };
};

export const createBotMessage = (message: string, type: messageStringType) => {
  return {
    owner: chatMessageOwner.BOT,
    type,
    message: message,
  };
};

export const createUserMessage = (message: string, type: messageStringType) => {
  return {
    owner: chatMessageOwner.USER,
    type,
    message: message,
  };
};

export const getSupportedCodecs = () => {
  const audioCodecSupportedTypes: string[] = [];
  const audioTypes = [
    'audio/webm',
    'audio/ogg',
    'audio/mp3',
    'audio/mp4',
    'audio/wav',
    'audio/x-matroska',
  ];
  const codecs = [
    'should-not-be-supported',
    'vp9',
    'vp9.0',
    'vp8',
    'vp8.0',
    'avc1',
    'av1',
    'h265',
    'h.265',
    'h264',
    'h.264',
    'opus',
    'pcm',
    'aac',
    'mpeg',
    'mp4a',
  ];
  audioTypes.forEach((mimeType) => {
    if (MediaRecorder.isTypeSupported(`${mimeType}`)) {
      audioCodecSupportedTypes.push(`${mimeType}`);
    }
    codecs.forEach((codec) => {
      if (MediaRecorder.isTypeSupported(`${mimeType};codecs=${codec}`)) {
        audioCodecSupportedTypes.push(`${mimeType};codecs=${codec}`);
      } else if (
        MediaRecorder.isTypeSupported(
          `${mimeType};codecs=${codec.toUpperCase()}`
        )
      ) {
        audioCodecSupportedTypes.push(
          `${mimeType};codecs=${codec.toUpperCase()}`
        );
      }
    });
  });
  return audioCodecSupportedTypes;
};

export const getSupportedVideoCodecs = () => {
  const containers = [
    'mp4',
    'webm',
    'ogg',
    'x-matroska',
    '3gpp',
    '3gpp2',
    '3gp2',
    'quicktime',
    'mpeg',
    'aac',
    'flac',
    'wav',
  ];
  const codecs = [
    'h264',
    'h.264',
    'opus',
    'vp9',
    'vp8',
    'avc1',
    'av1',
    'h265',
    'h.265',
    'pcm',
    'aac',
    'mpeg',
    'mp4a',
  ];
  const supportedVideos = containers
    .map((format) => `video/${format}`)
    .filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
  const supportedVideoCodecs = supportedVideos
    .flatMap((video) => codecs.map((codec) => `${video};codecs=${codec}`))
    .filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
  if (supportedVideos?.length) return supportedVideos[0];
};

export const getFirstSupportedVideoCodec = () => {
  const formatCodecCombinations = [
    // WebM with Opus audio (your test shows these work)
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',

    // MP4 combinations
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1,mp4a.40.2',
    'video/mp4;codecs=avc1.4D401E,mp4a.40.2',

    // Matroska as fallback
    'video/x-matroska;codecs=avc1,opus',
    'video/x-matroska;codecs=h264,opus',
    'video/x-matroska;codecs=vp8,opus',
  ];

  // Check specific codec combinations first
  for (const format of formatCodecCombinations) {
    if (MediaRecorder.isTypeSupported(format)) {
      return format;
    }
  }

  const containers = [
    'webm',
    'mp4',
    'ogg',
    'x-matroska',
    '3gpp',
    '3gpp2',
    '3gp2',
    'quicktime',
    'mpeg',
  ];

  const codecs = [
    'h264',
    'h.264',
    'opus',
    'vp9',
    'vp8',
    'avc1',
    'av1',
    'h265',
    'h.265',
    'aac',
    'mpeg',
    'mp4a',
  ];

  // Find the first supported video format
  for (const format of containers) {
    const mimeType = `video/${format}`;
    if (MediaRecorder.isTypeSupported(mimeType)) {
      // Find the first supported codec for this format
      // for (const codec of codecs) {
      //   const codecMimeType = `${mimeType};codecs=${codec}`;
      //   if (MediaRecorder.isTypeSupported(codecMimeType)) {
      //     return codecMimeType; // Return the first working format + codec
      //   }
      // }
      return mimeType; // If no codec is supported, return format only
    }
  }

  return null; // No supported format found
};

interface DragProps {
  firstElId: string;
  secondElId: string;
  resizeType: 'width' | 'height';
  currMousePos: number; // e.clientX for width, e.clientY for height
  minSize?: number;
  dividerSize?: number;
}

export const onDrag = ({
  firstElId,
  secondElId,
  resizeType,
  currMousePos,
  minSize = 10,
  dividerSize = 5,
}: DragProps) => {
  if (resizeType === 'width') {
    let leftEl = document.getElementById(firstElId);
    let rightEl = document.getElementById(secondElId);

    if (leftEl && rightEl) {
      let leftElWidth = parseInt(getComputedStyle(leftEl).width, minSize) || 0;
      let rightElWidth =
        parseInt(getComputedStyle(rightEl).width, minSize) || 0;
      const { left: leftElStart } = leftEl.getBoundingClientRect();

      const dx = currMousePos - leftElStart;
      const initWidths = leftElWidth + rightElWidth;
      const newLeftWidth = Math.min(
        Math.max(dx, minSize),
        initWidths - minSize
      );
      const newRightWidth = initWidths - newLeftWidth;
      return [newLeftWidth, newRightWidth];
    }
  } else {
    let topEl = document.getElementById(firstElId);
    let bottomEl = document.getElementById(secondElId);

    if (topEl && bottomEl) {
      let topElHeight = parseInt(getComputedStyle(topEl).height, minSize) || 0;
      let bottomElHeight =
        parseInt(getComputedStyle(bottomEl).height, minSize) || 0;
      const { top: topElStart } = topEl.getBoundingClientRect();

      const dx = currMousePos - topElStart;
      const initHeights = topElHeight + bottomElHeight;
      const newTopHeight = Math.min(
        Math.max(dx, minSize),
        initHeights - minSize
      );
      const newBottomHeight = initHeights - newTopHeight;
      return [newTopHeight, newBottomHeight];
    }
  }
  return [0, 0];
};

export const defaultWhiteLabelProps = {
  companyLogo: '/DesktopExpandedNavLogo.png',
  companyName: 'HireHunch',
  companyWebsite: 'https://hirehunch.com',
  companyFavicon: '',
  primaryColor: '#4738F5',
  privacyPolicyUrl: 'https://hirehunch.com/terms-and-conditions#privacy-policy',
  termsOfServiceUrl: 'https://hirehunch.com/terms-and-conditions',
  customerCare: 'care@hirehunch.com',
};

export function setFavicons(imageUrl: string) {
  if (!imageUrl) return;
  const icons = document.querySelectorAll('link[rel="icon"]');
  // for now, we are setting url link as received from back end
  icons.forEach((icon) => ((icon as HTMLLinkElement).href = imageUrl));
}

export const convertHexToRGBNumbers = (hex: string) => {
  hex = hex.replace(/^#/, '');

  // Parse the three components of the hex code
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  // Return the RGB string
  return `${r}, ${g}, ${b}`;
};
